class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const collectionRequest = UnewCollectionInfiniteScroll.beginUpdate();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent, collectionRequest)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, collectionRequest);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, collectionRequest) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent, collectionRequest);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent, collectionRequest) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent, collectionRequest);
  }

  static renderSection(html, event, updateEvent, collectionRequest = null) {
    if (!UnewCollectionInfiniteScroll.isCurrent(collectionRequest)) {
      updateEvent?.reject(new DOMException('Superseded collection update.', 'AbortError'));
      return;
    }
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    UnewCollectionInfiniteScroll.initialize();
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount?.innerHTML || "";
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.dataset.productCount = sourceCount?.dataset.productCount || '';
      container.dataset.totalCount = sourceCount?.dataset.totalCount || '';
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount?.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class UnewCollectionInfiniteScroll {
  static restorationContext() {
    const url = new URL(window.location.href);
    url.searchParams.sort();
    return url.pathname + url.search + url.hash;
  }

  static saveRestoration() {
    const instance = UnewCollectionInfiniteScroll.instance;
    if (!instance?.active()) return;
    const anchor = Array.from(instance.grid.children).find((card) => {
      const rect = card.getBoundingClientRect();
      return card.dataset.unewProductId && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
    });
    const state = history.state;
    if (state !== null && (typeof state !== 'object' || Array.isArray(state))) return;
    try {
      history.replaceState({ ...state, unewCollectionRestore: {
        context: this.restorationContext(),
        page: instance.page,
        anchor: anchor?.dataset.unewProductId || null,
        offset: anchor ? anchor.getBoundingClientRect().top : null,
        y: window.scrollY,
      } }, '', window.location.href);
    } catch (error) {}
  }

  static cancelRestoration(userInteraction = false) {
    const restore = this.restoration;
    if (!restore) return;
    restore.cancelled = true;
    restore.controls.abort();
    this.restoration = null;
    if (restore.loading) {
      const request = this.instance?.request;
      if (userInteraction && request && !request.signal.aborted) {
        const reason = new DOMException('Collection restoration cancelled by user interaction.', 'AbortError');
        this.expectedRestorationAborts.add(reason);
        request.abort(reason);
      } else {
        request?.abort();
      }
    }
  }

  static prepareRestoration() {
    const stored = history.state?.unewCollectionRestore;
    if (performance.getEntriesByType?.('navigation')[0]?.type !== 'back_forward' ||
        !stored || stored.context !== this.restorationContext() ||
        !Number.isInteger(stored.page) || stored.page < 1 ||
        !Number.isFinite(stored.y) || stored.y < 0 ||
        (stored.anchor !== null && (!/^\d+$/.test(stored.anchor) || !Number.isFinite(stored.offset)))) return;
    const restore = { ...stored, cancelled: false, controls: new AbortController() };
    this.restoration = restore;
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(type, () => this.cancelRestoration(true), {
        capture: true, passive: true, signal: restore.controls.signal,
      });
    }
  }

  static async restorePosition() {
    const restore = this.restoration;
    const instance = this.instance;
    if (!restore) return;
    const current = () => !restore.cancelled && this.restoration === restore &&
      instance?.active() && restore.context === this.restorationContext();
    try {
      if (!current() || restore.page < instance.page || restore.page > instance.lastPage) return;
      while (current() && instance.page < restore.page) {
        const page = instance.page;
        if (!instance.next) return;
        restore.loading = true;
        await instance.load();
        restore.loading = false;
        if (!current() || instance.page !== page + 1) return;
      }
      let anchor;
      const findAnchor = () => Array.from(instance.grid.children).find(
        (card) => card.dataset.unewProductId === restore.anchor
      );
      if (restore.anchor && !(anchor = findAnchor())) return;
      let previous;
      let stable = 0;
      for (let frame = 0; frame < 30 && current(); frame++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (!current()) return;
        anchor = restore.anchor ? findAnchor() : null;
        if (restore.anchor && !anchor) return;
        const rect = anchor?.getBoundingClientRect();
        const position = rect ? rect.top + window.scrollY : document.documentElement.scrollHeight;
        stable = previous !== undefined && Math.abs(position - previous) < 0.5 ? stable + 1 : 0;
        previous = position;
        if (stable >= 3) break;
      }
      if (!current() || stable < 3) return;
      const rect = anchor?.getBoundingClientRect();
      if (anchor && (!rect.height || !Number.isFinite(rect.top))) return;
      const target = anchor ? window.scrollY + rect.top - restore.offset : restore.y;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (!Number.isFinite(target) || target < 0 || target > max + 1) return;
      window.scrollTo({ top: target, left: window.scrollX, behavior: 'instant' });
    } finally {
      if (this.restoration === restore) this.cancelRestoration();
    }
  }

  static beginUpdate() {
    UnewCollectionInfiniteScroll.cancelRestoration();
    if (!document.querySelector('#ProductGridContainer [data-unew-pagination]')) return null;
    UnewCollectionInfiniteScroll.version += 1;
    UnewCollectionInfiniteScroll.instance?.destroy();
    UnewCollectionInfiniteScroll.instance = null;
    return UnewCollectionInfiniteScroll.version;
  }

  static isCurrent(version) {
    return version === null || version === UnewCollectionInfiniteScroll.version;
  }

  static initialize() {
    UnewCollectionInfiniteScroll.instance?.destroy();
    UnewCollectionInfiniteScroll.instance = null;
    const container = document.getElementById('ProductGridContainer');
    const marker = container?.querySelector('[data-unew-pagination]');
    if (!marker || !window.IntersectionObserver || !window.AbortController || !window.fetch) return;
    let instance;
    try {
      instance = new UnewCollectionInfiniteScroll(container, marker);
      UnewCollectionInfiniteScroll.instance = instance;
      instance.start();
    } catch (error) {
      instance?.destroy();
      UnewCollectionInfiniteScroll.instance = null;
      console.warn('Collection infinite scroll: pagination fallback retained.', error);
    }
  }

  constructor(container, marker) {
    this.container = container;
    this.marker = marker;
    this.version = UnewCollectionInfiniteScroll.version;
    this.grid = container.querySelector('#product-grid');
    this.pagination = container.querySelector('.pagination-wrapper');
    this.page = Number(marker.dataset.page);
    this.lastPage = Number(marker.dataset.pages);
    this.next = marker.dataset.nextUrl;
    this.pages = new Set([this.page]);
    this.products = new Set();
    this.loading = false;
    this.destroyed = false;
    this.busy = container.getAttribute('aria-busy');
    this.context = this.next ? this.urlContext(new URL(this.next, window.location.href)) : null;
    this.sentinel = document.createElement('div');
    this.sentinel.style.display = 'block';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.sentinel.style.height = '1px';
  }

  urlContext(url) {
    const params = new URLSearchParams(url.search);
    params.delete('page');
    params.delete('section_id');
    params.sort();
    return url.origin + url.pathname + '?' + params.toString();
  }

  nextURL(value, page = this.page, lastPage = this.lastPage) {
    if (!value) {
      if (page < lastPage) throw new Error('Missing next page URL.');
      return null;
    }
    const url = new URL(value, window.location.href);
    const nextPage = Number(url.searchParams.get('page'));
    if (
      url.origin !== window.location.origin ||
      url.pathname !== window.location.pathname ||
      this.urlContext(url) !== this.context ||
      !Number.isInteger(nextPage) ||
      nextPage !== page + 1 ||
      nextPage > lastPage ||
      this.pages.has(nextPage)
    ) throw new Error('Unexpected next page URL.');
    url.hash = '';
    return url;
  }

  cards(grid) {
    if (!grid) throw new Error('Missing product grid.');
    if (grid.tagName !== 'UL') {
      if (grid.classList.contains('collection--empty')) return [];
      throw new Error('Unexpected product grid.');
    }
    const cards = Array.from(grid.children);
    if (cards.some((card) => !card.matches('li.grid__item') || !/^\d+$/.test(card.dataset.unewProductId || ''))) {
      throw new Error('Missing product identity.');
    }
    return cards;
  }

  start() {
    if (!Number.isInteger(this.page) || this.page < 1 || !Number.isInteger(this.lastPage) || this.lastPage < this.page) {
      throw new Error('Invalid pagination state.');
    }
    this.cards(this.grid).forEach((card) => this.products.add(card.dataset.unewProductId));
    const nextURL = this.nextURL(this.next);
    if (this.lastPage > 1 && !this.pagination) throw new Error('Missing pagination fallback.');
    if (nextURL) {
      this.observer = new IntersectionObserver((entries) => {
        if (!UnewCollectionInfiniteScroll.restoration && entries.some((entry) => entry.isIntersecting)) this.load();
      }, { rootMargin: '0px 0px 600px 0px' });
      this.container.append(this.sentinel);
      this.observer.observe(this.sentinel);
    }
    this.hidePagination();
  }

  hidePagination() {
    if (!this.pagination) return;
    this.paginationStyle = this.pagination.getAttribute('style');
    this.paginationHidden = this.pagination.hidden;
    this.pagination.hidden = true;
    this.pagination.style.setProperty('display', 'none', 'important');
  }

  restorePagination() {
    if (!this.pagination || this.paginationHidden === undefined) return;
    this.pagination.hidden = this.paginationHidden;
    if (this.paginationStyle === null) this.pagination.removeAttribute('style');
    else this.pagination.setAttribute('style', this.paginationStyle);
    this.paginationHidden = undefined;
  }

  restoreBusy() {
    if (this.busy === null) this.container.removeAttribute('aria-busy');
    else this.container.setAttribute('aria-busy', this.busy);
  }

  active() {
    return !this.destroyed &&
      UnewCollectionInfiniteScroll.instance === this &&
      this.version === UnewCollectionInfiniteScroll.version &&
      this.container.isConnected &&
      this.container.querySelector('[data-unew-pagination]') === this.marker &&
      this.container.querySelector('#product-grid') === this.grid;
  }

  async load() {
    if (!this.active() || this.loading || !this.next) return;
    this.loading = true;
    this.observer.unobserve(this.sentinel);
    this.request = new AbortController();
    this.container.setAttribute('aria-busy', 'true');
    try {
      const url = this.nextURL(this.next);
      const page = Number(url.searchParams.get('page'));
      const response = await fetch(url.href, { signal: this.request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Collection page request failed.');
      if (response.url) {
        const actual = new URL(response.url);
        if (this.urlContext(actual) !== this.context || Number(actual.searchParams.get('page')) !== page) {
          throw new Error('Unexpected collection redirect.');
        }
      }
      const html = await response.text();
      if (!this.active()) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const container = parsed.getElementById('ProductGridContainer');
      const marker = container?.querySelector('[data-unew-pagination]');
      const grid = container?.querySelector('#product-grid');
      const lastPage = Number(marker?.dataset.pages);
      if (
        !marker || !grid || grid.dataset.id !== this.grid.dataset.id ||
        Number(marker.dataset.page) !== page || !Number.isInteger(lastPage) ||
        lastPage < page || lastPage > this.lastPage
      ) throw new Error('Unexpected collection page.');
      const next = marker.dataset.nextUrl;
      this.nextURL(next, page, lastPage);
      const pagination = container.querySelector('.pagination-wrapper');
      if (lastPage > 1 && !pagination) throw new Error('Missing fetched pagination fallback.');
      const cards = this.cards(grid);
      const seen = new Set(this.products);
      const additions = cards.filter((card) => {
        const id = card.dataset.unewProductId;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      additions.forEach((card) => {
        if (card.classList.contains('scroll-trigger')) card.classList.add('scroll-trigger--cancel');
      });
      if (!this.active()) return;
      if (this.grid.tagName === 'UL') {
        const fragment = document.createDocumentFragment();
        additions.forEach((card) => fragment.append(card));
        this.grid.append(fragment);
      } else if (additions.length) {
        const collection = grid.closest('.collection');
        if (!collection || collection === grid) throw new Error('Missing fetched collection wrapper.');
        cards.forEach((card) => {
          if (!additions.includes(card)) card.remove();
        });
        pagination?.remove();
        this.grid.replaceWith(collection);
        this.grid = this.container.querySelector('#product-grid');
      }
      this.restorePagination();
      if (pagination) {
        if (this.pagination?.isConnected) this.pagination.replaceWith(pagination);
        else this.grid.closest('.collection').append(pagination);
      } else {
        this.pagination?.remove();
      }
      this.pagination = pagination;
      this.hidePagination();
      this.products = seen;
      this.pages.add(page);
      this.page = page;
      this.lastPage = lastPage;
      this.next = next;
      this.marker.dataset.page = String(page);
      this.marker.dataset.pages = String(lastPage);
      this.marker.dataset.nextUrl = next;
      if (!next) {
        this.observer.disconnect();
        this.sentinel.remove();
      }
    } catch (error) {
      if (!this.active()) return;
      if (error.name === 'AbortError' && this.request.signal.aborted) return;
      UnewCollectionInfiniteScroll.cancelRestoration();
      this.destroy();
      console.warn('Collection infinite scroll: use pagination to continue.', error);
    } finally {
      this.loading = false;
      if (this.active()) {
        this.restoreBusy();
        if (this.next) this.observer.observe(this.sentinel);
      }
    }
  }

  destroy() {
    this.destroyed = true;
    this.request?.abort();
    this.observer?.disconnect();
    this.sentinel.remove();
    this.restorePagination();
    this.restoreBusy();
  }
}

UnewCollectionInfiniteScroll.expectedRestorationAborts = new WeakSet();
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.name === 'AbortError' &&
      UnewCollectionInfiniteScroll.expectedRestorationAborts.has(event.reason)) {
    event.preventDefault();
  }
});
UnewCollectionInfiniteScroll.version = 0;
UnewCollectionInfiniteScroll.instance = null;
UnewCollectionInfiniteScroll.prepareRestoration();
UnewCollectionInfiniteScroll.initialize();
window.addEventListener('pagehide', () => {
  UnewCollectionInfiniteScroll.saveRestoration();
  UnewCollectionInfiniteScroll.cancelRestoration();
  if (!document.querySelector('#ProductGridContainer [data-unew-pagination]')) return;
  UnewCollectionInfiniteScroll.version += 1;
  const instance = UnewCollectionInfiniteScroll.instance;
  instance?.request?.abort();
  instance?.observer?.disconnect();
});
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    UnewCollectionInfiniteScroll.cancelRestoration();
    UnewCollectionInfiniteScroll.initialize();
  } else {
    UnewCollectionInfiniteScroll.restorePosition();
  }
});
document.addEventListener('shopify:section:load', () => UnewCollectionInfiniteScroll.initialize());
